import React, { useMemo, useState } from "react";
import { HUD_PRESETS, HUD_WIDGET_TYPES, defaultHud, spriteFromMap } from "../../lib/pixelArt";
import { projects as projectsApi } from "../../lib/api";
import { toast } from "sonner";
import { Heart, SquaresFour, UserCircle, Gauge } from "@phosphor-icons/react";

const CANVAS_W = 720;
const CANVAS_H = 480;
const SCALE = 0.72;

export default function HudDesignerPanel({ project, onChange }) {
  const cfg = project.config || {};
  const hud = cfg.hud?.widgets ? cfg.hud : defaultHud(project.config?.camera === "topdown" ? "gungeon" : "rpg");
  const [selected, setSelected] = useState(hud.widgets[0]?.id || "");
  const current = hud.widgets.find((w) => w.id === selected) || hud.widgets[0];

  const setHud = (patch) => onChange({ config: { ...cfg, hud: { ...hud, ...patch } } });
  const setWidgets = (widgets) => setHud({ widgets });
  const update = (id, patch) => setWidgets(hud.widgets.map((w) => (w.id === id ? { ...w, ...patch } : w)));

  const add = (type) => {
    const w = { id: crypto.randomUUID(), type, x: 40, y: 40, count: 4, w: 140, h: 12, color: "#e23b4a", label: type };
    setWidgets([...hud.widgets, w]);
    setSelected(w.id);
  };

  const applyPreset = (id) => {
    const next = defaultHud(id);
    setHud(next);
    setSelected(next.widgets[0]?.id || "");
    toast.success("Preset de HUD aplicado");
  };

  const saveWidgetsAsSprites = async () => {
    const maps = { hearts: "heart", bar: "bar", portrait: "portrait", slots: "slot" };
    for (const w of hud.widgets) {
      const map = maps[w.type];
      if (!map) continue;
      await projectsApi.addSprite(project.project_id, {
        name: `HUD_${w.type}`,
        data_url: spriteFromMap(map),
        category: w.type,
        studio: "hud",
        width: 32,
        height: 32,
      }).catch(() => {});
    }
    const p = await projectsApi.get(project.project_id).catch(() => null);
    if (p) onChange({ sprites: p.sprites });
    toast.success("Iconos de HUD en la biblioteca");
  };

  const portrait = (project.sprites || []).find((s) => s.name === "HUD_retrato" || s.category === "portrait");
  const heartSpr = (project.sprites || []).find((s) => s.name === "HUD_heart" || s.category === "heart");
  const weaponSpr = (project.sprites || []).find((s) => s.studio === "gear" || s.category === "weapon");

  const preview = useMemo(() => hud.widgets, [hud.widgets]);

  return (
    <div className="p-4 grid grid-cols-1 xl:grid-cols-[220px_1fr_240px] gap-4">
      <div className="space-y-3">
        <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--gc-muted)" }}>Presets</div>
        {HUD_PRESETS.map((p) => (
          <button
            key={p.id}
            onClick={() => applyPreset(p.id)}
            className="w-full text-left rounded-2xl border p-3"
            style={{
              borderColor: hud.preset === p.id ? "var(--gc-primary)" : "var(--gc-border)",
              background: hud.preset === p.id ? "var(--gc-primary-soft)" : "var(--gc-surface)",
            }}
          >
            <div className="text-sm font-semibold">{p.name}</div>
            <div className="text-[11px] mt-0.5" style={{ color: "var(--gc-muted)" }}>{p.hint}</div>
          </button>
        ))}
        <div className="text-xs font-semibold uppercase tracking-widest pt-2" style={{ color: "var(--gc-muted)" }}>Añadir</div>
        <div className="flex flex-wrap gap-1.5">
          {HUD_WIDGET_TYPES.map((t) => (
            <button key={t.id} className="gc-chip" onClick={() => add(t.id)}>{t.label}</button>
          ))}
        </div>
        <button className="gc-btn gc-btn-ghost h-9 px-3 w-full text-xs" onClick={saveWidgetsAsSprites}>
          Guardar iconos HUD
        </button>
      </div>

      <div>
        <div className="text-sm font-semibold mb-2">Vista previa 720×480 — se usa en el motor</div>
        <div
          className="relative overflow-hidden rounded-2xl border"
          style={{
            width: CANVAS_W * SCALE,
            height: CANVAS_H * SCALE,
            background: "#0b1220",
            borderColor: "var(--gc-border)",
          }}
        >
          <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "linear-gradient(#1e293b 1px, transparent 1px), linear-gradient(90deg, #1e293b 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
          {preview.map((w) => (
            <button
              key={w.id}
              type="button"
              onClick={() => setSelected(w.id)}
              className="absolute text-left"
              style={{
                left: w.x * SCALE,
                top: w.y * SCALE,
                outline: selected === w.id ? "2px solid #22d3ee" : "none",
                zIndex: 2,
              }}
            >
              <WidgetPreview w={w} heartSpr={heartSpr} portrait={portrait} weaponSpr={weaponSpr} scale={SCALE} />
            </button>
          ))}
        </div>
        <p className="text-[11px] mt-2" style={{ color: "var(--gc-muted)" }}>
          Pulsa un widget para moverlo. El playtest lee este layout (corazones = HP).
        </p>
      </div>

      <div className="space-y-3">
        <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--gc-muted)" }}>Widget</div>
        {!current && <p className="text-sm" style={{ color: "var(--gc-muted)" }}>Añade un widget.</p>}
        {current && (
          <>
            <div className="text-sm font-semibold">{HUD_WIDGET_TYPES.find((t) => t.id === current.type)?.label || current.type}</div>
            {["x", "y"].map((k) => (
              <label key={k} className="text-xs block" style={{ color: "var(--gc-muted)" }}>
                {k.toUpperCase()}
                <input
                  type="number"
                  className="gc-input h-8 w-full mt-1"
                  value={current[k] ?? 0}
                  onChange={(e) => update(current.id, { [k]: Number(e.target.value) })}
                />
              </label>
            ))}
            {(current.type === "hearts" || current.type === "slots") && (
              <label className="text-xs block" style={{ color: "var(--gc-muted)" }}>
                Cantidad
                <input
                  type="number"
                  min={1}
                  max={10}
                  className="gc-input h-8 w-full mt-1"
                  value={current.count || 4}
                  onChange={(e) => update(current.id, { count: Number(e.target.value) })}
                />
              </label>
            )}
            {current.type === "bar" && (
              <>
                <label className="text-xs block" style={{ color: "var(--gc-muted)" }}>
                  Ancho
                  <input type="number" className="gc-input h-8 w-full mt-1" value={current.w || 140} onChange={(e) => update(current.id, { w: Number(e.target.value) })} />
                </label>
                <label className="text-xs block" style={{ color: "var(--gc-muted)" }}>
                  Color
                  <input type="color" className="h-8 w-full mt-1 rounded-lg" value={current.color || "#e23b4a"} onChange={(e) => update(current.id, { color: e.target.value })} />
                </label>
              </>
            )}
            <button className="gc-chip gc-chip-danger" onClick={() => setWidgets(hud.widgets.filter((w) => w.id !== current.id))}>Quitar</button>
          </>
        )}
        <div className="flex gap-2 text-[11px] pt-4" style={{ color: "var(--gc-muted)" }}>
          <Heart size={14} /> <SquaresFour size={14} /> <UserCircle size={14} /> <Gauge size={14} />
        </div>
      </div>
    </div>
  );
}

function WidgetPreview({ w, heartSpr, portrait, weaponSpr, scale }) {
  if (w.type === "hearts") {
    return (
      <div className="flex gap-0.5">
        {Array.from({ length: w.count || 4 }).map((_, i) => (
          heartSpr
            ? <img key={i} src={heartSpr.data_url} alt="" style={{ width: 18 * scale * 1.4, height: 18 * scale * 1.4, imageRendering: "pixelated" }} />
            : <span key={i} style={{ color: "#e23b4a", fontSize: 14 }}>♥</span>
        ))}
      </div>
    );
  }
  if (w.type === "bar") {
    return (
      <div>
        <div style={{ fontSize: 9, color: "#94a3b8", marginBottom: 2 }}>{w.label || "HP"}</div>
        <div style={{ width: (w.w || 140) * scale, height: (w.h || 10) * scale, background: "#1e293b", border: "1px solid #334155", borderRadius: 4, overflow: "hidden" }}>
          <div style={{ width: "70%", height: "100%", background: w.color || "#e23b4a" }} />
        </div>
      </div>
    );
  }
  if (w.type === "portrait") {
    return (
      <div style={{ width: 48 * scale, height: 48 * scale, border: "2px solid #e8f0ff", background: "#0f172a" }}>
        {portrait && <img src={portrait.data_url} alt="" className="w-full h-full" style={{ imageRendering: "pixelated" }} />}
      </div>
    );
  }
  if (w.type === "score") {
    return <div style={{ fontFamily: "monospace", color: "#facc15", fontSize: 13, fontWeight: 700 }}>SCORE 0000</div>;
  }
  if (w.type === "slots") {
    return (
      <div className="flex gap-1">
        {Array.from({ length: w.count || 4 }).map((_, i) => (
          <div key={i} style={{ width: 28 * scale, height: 28 * scale, border: "2px solid #334155", background: "#0f172a" }} />
        ))}
      </div>
    );
  }
  if (w.type === "weapon") {
    return (
      <div style={{ width: 40 * scale, height: 40 * scale, border: "2px solid #e0b040", background: "#0f172a" }}>
        {weaponSpr && <img src={weaponSpr.data_url} alt="" className="w-full h-full" style={{ imageRendering: "pixelated" }} />}
      </div>
    );
  }
  if (w.type === "boss") {
    return (
      <div>
        <div style={{ fontSize: 9, color: "#f87171" }}>BOSS</div>
        <div style={{ width: 220 * scale, height: 8 * scale, background: "#1e293b", border: "1px solid #7f1d1d" }}>
          <div style={{ width: "55%", height: "100%", background: "#ef4444" }} />
        </div>
      </div>
    );
  }
  return <span className="gc-chip">{w.type}</span>;
}
